import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, BdPageableHelper, Paginator } from '@bindoc-projects/platform-lib/data';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import {
  AysClinicByConditionResponse,
  AysClinicByConditionResult,
  AysClinicByDistanceResponse,
  AysClinicByDistanceResult,
  AysClinicAccessibilitySection,
  AysClinicAtmpSection,
  AysClinicCaseCount,
  AysClinicCatalogKey,
  AysClinicCodeView,
  AysClinicCaseNumbersSection,
  AysClinicContactsSection,
  AysClinicEmergencySection,
  AysClinicEquipmentSection,
  AysClinicLocationReference,
  AysClinicMedicalServicesSection,
  AysClinicMetadata,
  AysClinicMetadataResponse,
  AysClinicNonMedicalServicesSection,
  AysClinicQualityAssuranceSection,
  AysClinicHygieneSection,
  AysClinicGeneralSection,
  AysClinicTeachingEducationSection,
  AysClinicQualityManagementSection,
  AysClinicRiskManagementSection,
  AysClinicAnnualAccountSection,
  AysClinicQsffxSection,
  AysClinicPppSection,
  AysClinicStaffingSection,
  AysClinicDeqsArea,
  AysClinicOverview,
  AysClinicOverviewResponse,
  AysClinicSearchCriteria,
  AysClinicSearchResult,
  AysClinicSearchResultResponse,
  AysDepartmentDetail,
  AysDepartmentIndexItem,
  AysOperatingNumberTimeSeries,
  AysOperatingNumberTimeSeriesResponse,
  AysClinicAvailableClinicLevels,
  ClinicHandDisinfectionUsage,
} from '@bindoc-projects/clinic-domain-lib';
import { Observable, of,
  forkJoin,
} from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AysClinicAvailableClinicLevelsResponse } from "./available-clinic-levels-response";
import { AysClinicSnStore } from "../store/clinic-sn.store";

/**
 * Service for the SN-only clinics-api surface.
 *
 * Discovery flow: {@link search} -> {@link metadata} / {@link overview} ->
 * {@link availableQbYears} (feeds the year picker) -> display sections.
 *
 * `year` is optional on every endpoint; when omitted the backend defaults to
 * the latest QB year. Display endpoints answer a known clinic that has no QB
 * for the requested year with a 200 + empty body, which is surfaced here as
 * `null`; an unknown clinic (404) propagates as an error.
 */
/**
 * QB CLINIC-grain operating numbers backing hand-disinfectant consumption
 * (ml/Patiententag), one per ward type. Ids from `/clinics/available-operating-numbers`.
 */
const HAND_DISINFECTION_NORMAL_STATIONS_OPERATING_NUMBER =
  'handDisinfectionMlNormalStations';
const HAND_DISINFECTION_ICU_OPERATING_NUMBER = 'handDisinfectionMlIcu';
/** QB catalog family (key prefix `AA…`) for special equipment. */
const SPECIAL_EQUIPMENT_CATALOG_FAMILY = 'AA';

@Injectable({
  providedIn: 'root',
})
export class AysClinicSnService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _store = new AysClinicSnStore(
    this._environmentService.load(ApiEndpointKey.AYS_CLINIC_RESOURCE_URL),
  );

  /**
   * Search clinics by name/city/IK/clinicSn, optionally narrowed by any of the
   * server-side {@link AysClinicSearchCriteria} (all combinable).
   * @param q search term
   * @param opts search criteria ({@link AysClinicSearchCriteria}) plus `page` for pagination
   */
  public search(
    q: string,
    opts: AysClinicSearchCriteria & { page?: Paginator } = {},
  ): Observable<BdPageable<AysClinicSearchResult>> {
    let params = Paginator.toHttpParams(
      opts.page ?? Paginator.of({ pageSize: 20, pageIndex: 0 }),
    );
    params = params.set('q', q);

    if (opts.onlyLive != null) {
      params = params.set('onlyLive', opts.onlyLive.toString());
    }

    if (opts.hasQbPublished != null) {
      params = params.set('hasQbPublished', opts.hasQbPublished.toString());
    }

    if (opts.traeger) {
      params = params.set('traeger', opts.traeger);
    }

    if (opts.bundesland !== undefined && opts.bundesland !== null) {
      params = params.set('bundesland', opts.bundesland.toString());
    }

    if (opts.years?.length) {
      params = params.appendAll({
        years: opts.years.map((year) => year.toString()),
      });
    }

    return this._httpClient
      .get<BdPageable<AysClinicSearchResultResponse>>(this._store.search(), {
        params,
      })
      .pipe(
        map((res) =>
          BdPageableHelper.responseToPage(res, AysClinicSearchResult.fromObj),
        ),
      );
  }

  /**
   * Clinics within `distanceInMeters` of a GPS point
   * (`GET /clinics/by-distance/`) — powers the "Kliniken in Ihrer Nähe" list.
   * The endpoint is legacy-shaped (composite `clinic.id`), so each result's
   * opaque `clinicSn` is resolved client-side (see {@link AysClinicByDistanceResult}).
   * When `years` is omitted the backend defaults to the latest QB year.
   */
  public byDistance(
    latitude: number,
    longitude: number,
    distanceInMeters = 30_000,
    years?: number[],
  ): Observable<AysClinicByDistanceResult[]> {
    let params = new HttpParams()
      .set('center', `[${longitude}, ${latitude}]`)
      .set('distance', distanceInMeters.toString());

    if (years?.length) {
      params = params.appendAll({ years: years.map((year) => year.toString()) });
    }

    return this._httpClient
      .get<AysClinicByDistanceResponse[]>(this._store.byDistance(), { params })
      .pipe(map((res) => (res ?? []).map(AysClinicByDistanceResult.fromObj)));
  }

  /**
   * Batch-fetch specific clinics by their `clinicSn`
   * (`POST /clinics/by-condition` with a `clinicSn` content-condition). The
   * condition accepts opaque clinicSn strings — iNEK Standortnummern and
   * synthetic `L-{ik}-{sn}` ids alike — so no client-side id translation is
   * needed. Returns one row per (clinic × QB year); callers dedupe by clinicSn.
   */
  public byClinicSns(
    clinicSns: string[],
  ): Observable<AysClinicByConditionResult[]> {
    // An empty `includes` is NOT an empty result server-side: the condition
    // matches nothing and is therefore skipped, so the endpoint answers with
    // EVERY clinic (~77k rows / ~38 MB). Callers legitimately pass an empty
    // list (a scenario with no target clinics, an unselected form), so short-
    // circuit here rather than relying on each of them to remember.
    if (clinicSns.length === 0) {
      return of([]);
    }

    const condition = {
      clinicSn: { includes: clinicSns, excludes: null },
    };

    return this._httpClient
      .post<
        AysClinicByConditionResponse[]
      >(this._store.byCondition(), condition)
      .pipe(map((res) => (res ?? []).map(AysClinicByConditionResult.fromObj)));
  }

  /**
   * All clinics in the given Bundesländer, via the `stateId` LIST condition on
   * `POST /clinics/by-condition`. Returns one row per (clinic × QB year) exactly
   * like {@link byClinicSns}; callers dedupe by `clinicSn` (latest `year`). Each
   * row carries the clinic's coordinates on `address.location`, so this is the
   * SN-native source for an all-clinics map (there is no "all clinics" GET on the
   * SN surface — `search` requires a query and is paginated).
   * @param stateIds zero-padded 2-digit Bundesland codes (`01`…`16`)
   */
  public byStateIds(
    stateIds: string[],
  ): Observable<AysClinicByConditionResult[]> {
    const condition = {
      stateId: { includes: stateIds, excludes: null },
    };

    return this._httpClient
      .post<
        AysClinicByConditionResponse[]
      >(this._store.byCondition(), condition)
      .pipe(map((res) => (res ?? []).map(AysClinicByConditionResult.fromObj)));
  }

  /** Registry metadata for a clinic. `null` when the clinic has no QB for the year. */
  public metadata(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicMetadata | null> {
    return this._display<AysClinicMetadataResponse, AysClinicMetadata>(
      this._store.metadata(clinicSn),
      AysClinicMetadata.fromObj,
      year,
    );
  }

  /** Headline per-year metrics. `null` when the clinic has no QB for the year. */
  public overview(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicOverview | null> {
    return this._display<AysClinicOverviewResponse, AysClinicOverview>(
      this._store.overview(clinicSn),
      AysClinicOverview.fromObj,
      year,
    );
  }

  /**
   * Years for which a Qualitätsbericht exists — source of truth for the year
   * picker. The API returns the years as strings; they are parsed to numbers here.
   */
  public availableQbYears(clinicSn: string): Observable<number[]> {
    return this._httpClient
      .get<string[]>(this._store.availableQbYears(clinicSn))
      .pipe(map((res) => (res ?? []).map((year) => parseInt(year, 10))));
  }

  /** Related locations (main/other/child). `null` when unavailable for the year. */
  public locations(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicLocationReference | null> {
    return this._section<AysClinicLocationReference>(
      this._store.locations(clinicSn),
      year,
    );
  }

  /** Specialty-department index. `null` when the clinic has no QB for the year. */
  public departments(
    clinicSn: string,
    year?: number,
  ): Observable<AysDepartmentIndexItem[] | null> {
    return this._section<AysDepartmentIndexItem[]>(
      this._store.departments(clinicSn),
      year,
    );
  }

  /** In-flight/settled department detail per (clinicSn, sdKey, year). */
  private readonly _departmentCache = new Map<
    string,
    Observable<AysDepartmentDetail | null>
  >();

  /**
   * Detail of a single specialty department (`DepartmentDetailSection`). `null`
   * when the clinic has no QB for the year.
   *
   * Cached and shared per (clinicSn, sdKey, year): the SN-native Fachabteilung
   * page splits this one fat payload across an Übersicht landing page and its
   * sub-pages (Personal, Leistungen, Ärztliche Leitung, …), so without sharing,
   * navigating between them would re-fetch the whole payload each time.
   */
  public department(
    clinicSn: string,
    sdKey: string | number,
    year?: number,
  ): Observable<AysDepartmentDetail | null> {
    const cacheKey = `${clinicSn}|${sdKey}|${year ?? ''}`;
    let cached = this._departmentCache.get(cacheKey);

    if (!cached) {
      cached = this._section<AysDepartmentDetail>(
        this._store.department(clinicSn, sdKey),
        year,
      ).pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this._departmentCache.set(cacheKey, cached);
    }

    return cached;
  }

  /**
   * Paged per-clinic case counts for one code view — a coding system, or the
   * `OPS-115B` §115b filtered view of the OPS codes (see
   * {@link AysClinicCodeView}).
   * @param opts `year` scopes the QB year; `q` filters by code; `page` paginates.
   */
  public codes(
    clinicSn: string,
    codeView: AysClinicCodeView,
    opts: { year?: number; q?: string; page?: Paginator } = {},
  ): Observable<BdPageable<AysClinicCaseCount>> {
    return this._fetchCodes(this._store.codes(clinicSn, codeView), opts);
  }

  /**
   * Paged per-department case counts for one code view
   * (`GET /clinics/{clinicSn}/departments/{sdKey}/codes/{codeView}`) — the
   * department-grained counterpart of {@link codes}, returning the same
   * `PageCaseObject` shape scoped to one specialty department.
   * @param opts `year` scopes the QB year; `q` filters by code; `page` paginates.
   */
  public departmentCodes(
    clinicSn: string,
    sdKey: string | number,
    codeView: AysClinicCodeView,
    opts: { year?: number; q?: string; page?: Paginator } = {},
  ): Observable<BdPageable<AysClinicCaseCount>> {
    return this._fetchCodes(
      this._store.departmentCodes(clinicSn, sdKey, codeView),
      opts,
    );
  }

  /** Shared paged codes GET for the per-clinic and per-department endpoints. */
  private _fetchCodes(
    url: string,
    opts: { year?: number; q?: string; page?: Paginator },
  ): Observable<BdPageable<AysClinicCaseCount>> {
    let params = Paginator.toHttpParams(
      opts.page ?? Paginator.of({ pageSize: 20, pageIndex: 0 }),
    );

    if (opts.year !== undefined && opts.year !== null) {
      params = params.set('year', opts.year.toString());
    }

    if (opts.q !== undefined) {
      params = params.set('q', opts.q);
    }

    return this._httpClient.get<BdPageable<AysClinicCaseCount>>(url, { params });
  }

  /**
   * A single operating number's time series for a clinic
   * (`GET /clinics/{clinicSn}/timeseries/{operatingNumber}`) — one value across
   * all of the clinic's QB years. The endpoint carries the full year history
   * and is not year-scoped.
   *
   * Deliberately one metric per call: each operating number is fetched
   * independently so a slow or rejected metric never blocks the others and each
   * can drive its own loading state. `null` when the clinic never reported it
   * or the backend rejects the id (e.g. a 400 for a non-CLINIC-grain metric).
   */
  public timeseries(
    clinicSn: string,
    operatingNumber: string,
  ): Observable<AysOperatingNumberTimeSeries | null> {
    return this._httpClient
      .get<AysOperatingNumberTimeSeriesResponse | null>(
        this._store.timeseries(clinicSn, operatingNumber),
      )
      .pipe(
        map((res) =>
          res
            ? AysOperatingNumberTimeSeries.fromObj(operatingNumber, res)
            : null,
        ),
        catchError(() => of(null)),
      );
  }

  /** In-flight/settled catalog per family, so each catalog is fetched at most once. */
  private readonly _catalogCache = new Map<
    string,
    Observable<AysClinicCatalogKey[]>
  >();

  /**
   * QB catalog for a family (the alphabetic prefix of a key, e.g. `AA`, `MP`,
   * `VC`). Cached and shared across sections so a family is fetched at most
   * once; a failed request resolves to an empty catalog (keys fall back to
   * their raw code). Callers should request this only when a section is on
   * screen, so the network cost tracks what the user actually sees.
   */
  public catalogKeys(family: string): Observable<AysClinicCatalogKey[]> {
    let cached = this._catalogCache.get(family);

    if (!cached) {
      cached = this._httpClient
        .get<AysClinicCatalogKey[]>(this._store.catalogKeys(family))
        .pipe(
          catchError(() => of<AysClinicCatalogKey[]>([])),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this._catalogCache.set(family, cached);
    }

    return cached;
  }

  /** Case-number totals + medical-care-level classification. `null` when unavailable. */
  public caseNumbers(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicCaseNumbersSection | null> {
    return this._section<AysClinicCaseNumbersSection>(
      this._store.section(clinicSn, 'case-numbers'),
      year,
    );
  }

  /** `contacts` content section. `null` when unavailable for the year. */
  public contacts(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicContactsSection | null> {
    return this._section<AysClinicContactsSection>(
      this._store.section(clinicSn, 'contacts'),
      year,
    );
  }

  /** `accessibility` content section. `null` when unavailable for the year. */
  public accessibility(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicAccessibilitySection | null> {
    return this._section<AysClinicAccessibilitySection>(
      this._store.section(clinicSn, 'accessibility'),
      year,
    );
  }

  /** `atmp` content section. `null` when unavailable for the year. */
  public atmp(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicAtmpSection | null> {
    return this._section<AysClinicAtmpSection>(
      this._store.section(clinicSn, 'atmp'),
      year,
    );
  }

  /** `emergency` content section. `null` when unavailable for the year. */
  public emergency(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicEmergencySection | null> {
    return this._section<AysClinicEmergencySection>(
      this._store.section(clinicSn, 'emergency'),
      year,
    );
  }

  /** `equipment` content section. `null` when unavailable for the year. */
  public equipment(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicEquipmentSection | null> {
    return this._section<AysClinicEquipmentSection>(
      this._store.section(clinicSn, 'equipment'),
      year,
    );
  }

  /** `medical-services` content section. `null` when unavailable for the year. */
  public medicalServices(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicMedicalServicesSection | null> {
    return this._section<AysClinicMedicalServicesSection>(
      this._store.section(clinicSn, 'medical-services'),
      year,
    );
  }

  /** `non-medical-services` content section. `null` when unavailable for the year. */
  public nonMedicalServices(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicNonMedicalServicesSection | null> {
    return this._section<AysClinicNonMedicalServicesSection>(
      this._store.section(clinicSn, 'non-medical-services'),
      year,
    );
  }

  /** `quality-assurance` content section (QB Teil C). `null` when unavailable. */
  public qualityAssurance(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicQualityAssuranceSection | null> {
    return this._section<AysClinicQualityAssuranceSection>(
      this._store.section(clinicSn, 'quality-assurance'),
      year,
    );
  }

  /** `hygiene` content section (Hygiene). `null` when unavailable for the year. */
  public hygiene(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicHygieneSection | null> {
    return this._section<AysClinicHygieneSection>(
      this._store.section(clinicSn, 'hygiene'),
      year,
    );
  }

  /** `general` content section (Allgemeine Angaben). `null` when unavailable. */
  public general(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicGeneralSection | null> {
    return this._section<AysClinicGeneralSection>(
      this._store.section(clinicSn, 'general'),
      year,
    );
  }

  /** `quality-management` content section. `null` when unavailable for the year. */
  public qualityManagement(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicQualityManagementSection | null> {
    return this._section<AysClinicQualityManagementSection>(
      this._store.section(clinicSn, 'quality-management'),
      year,
    );
  }

  /** `teaching-education` content section. `null` when unavailable for the year. */
  public teachingEducation(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicTeachingEducationSection | null> {
    return this._section<AysClinicTeachingEducationSection>(
      this._store.section(clinicSn, 'teaching-education'),
      year,
    );
  }

  /** `risk-management` content section. `null` when unavailable for the year. */
  public riskManagement(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicRiskManagementSection | null> {
    return this._section<AysClinicRiskManagementSection>(
      this._store.section(clinicSn, 'risk-management'),
      year,
    );
  }

  /** `ppp` Psychiatrie-Personalausstattung (PPP-RL). `null` when unavailable. */
  public ppp(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicPppSection | null> {
    return this._section<AysClinicPppSection>(
      this._store.section(clinicSn, 'ppp'),
      year,
    );
  }

  /** `staffing` Personal & Personalausstattung QB section. `null` when unavailable. */
  public staffing(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicStaffingSection | null> {
    return this._section<AysClinicStaffingSection>(
      this._store.section(clinicSn, 'staffing'),
      year,
    );
  }

  /**
   * `annual-account` Jahresabschluss (Finanzkennzahlen).
   *
   * Requires `ANNUAL_ACCOUNTS:BASE` — without it the endpoint answers 403, so
   * call sites gate the block rather than rendering an error.
   *
   * A clinic that filed nothing for the requested year still answers 200 with
   * all 24 positions present and every value null; use the response's
   * `availableYears` (independent of the QB years) to tell "no Jahresabschluss
   * this year" apart from "none at all".
   */
  public annualAccount(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicAnnualAccountSection | null> {
    return this._section<AysClinicAnnualAccountSection>(
      this._store.section(clinicSn, 'annual-account'),
      year,
    );
  }

  /** `qsffx` Hüftfraktur QS (QSFFx-RL). `null` when unavailable for the year. */
  public qsffx(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicQsffxSection | null> {
    return this._section<AysClinicQsffxSection>(
      this._store.section(clinicSn, 'qsffx'),
      year,
    );
  }

  /** `deqs` Leistungsbereiche (datengestützte QS). `null` when unavailable. */
  public deqs(
    clinicSn: string,
    year?: number,
  ): Observable<AysClinicDeqsArea[] | null> {
    return this._section<AysClinicDeqsArea[]>(
      this._store.section(clinicSn, 'deqs'),
      year,
    );
  }

  /**
   * Year-scoped GET of an opaque display section that maps a 200 + empty body
   * to `null`. Thin wrapper over {@link _display} with an identity mapper for
   * sections whose response needs no transform.
   */
  private _section<T>(url: string, year?: number): Observable<T | null> {
    return this._display<T, T>(url, (value) => value, year);
  }

  private _display<R, T>(
    url: string,
    mapFn: (obj: R) => T,
    year?: number,
  ): Observable<T | null> {
    return this._httpClient
      .get<R>(url, { params: this._yearParams(year) })
      .pipe(map((res) => (res == null ? null : mapFn(res))));
  }

  private _yearParams(year?: number): HttpParams {
    let params = new HttpParams();

    if (year !== undefined && year !== null) {
      params = params.set('year', year.toString());
    }

    return params;
  }

  public availableClinicLevels(): Observable<AysClinicAvailableClinicLevels> {
    const url = this._store.availableClinicLevels();

    return this._httpClient
      .get<AysClinicAvailableClinicLevelsResponse>(url)
      .pipe(
        map((response) => ({
          ...response,
          information: {
            ...response.information,
            date: new Date(response.information.date),
          },
        })),
      );
  }
  /**
   * Global special-equipment catalog for the filter picklist, sourced from the
   * SN-native QB catalog (`/clinics/catalog-keys/AA`). The legacy per-clinic
   * `/special-equipment/available` endpoint is gone on the SN surface. Requires
   * the target env to serve the SN clinics-api (BDA-5749 / edge-api-gateway).
   */
  public availableSpecialEquipment(): Observable<
    {
      key: string;
      description: string;
    }[]
  > {
    return this.catalogKeys(SPECIAL_EQUIPMENT_CATALOG_FAMILY).pipe(
      map((keys) =>
        keys.map((entry) => ({
          key: entry.key,
          description: entry.label ?? '',
        })),
      ),
    );
  }
  /**
   * Hand-disinfectant consumption per year, sourced from the SN-native QB time
   * series (`/clinics/{clinicSn}/timeseries/{operatingNumber}`) — one call per
   * ward type — and merged into the legacy per-year shape. Requires the target
   * env to serve the SN clinics-api surface (BDA-5749 / edge-api-gateway).
   */
  public handDisinfectionUsage(
    clinicSn: string,
  ): Observable<ClinicHandDisinfectionUsage> {

    return forkJoin([
      this.timeseries(
        clinicSn,
        HAND_DISINFECTION_NORMAL_STATIONS_OPERATING_NUMBER,
      ),
      this.timeseries(clinicSn, HAND_DISINFECTION_ICU_OPERATING_NUMBER),
    ]).pipe(
      map(([normalStations, intensiveCareUnit]) =>
        this._toHandDisinfectionUsage(normalStations, intensiveCareUnit),
      ),
    );
  }

  /**
   * Merge the normal-station and ICU hand-disinfectant time series into the
   * legacy per-year `ClinicHandDisinfectionUsage` map. A year present in either
   * series appears in the result; a metric missing for a year is left absent.
   */
  private _toHandDisinfectionUsage(
    normalStations: AysOperatingNumberTimeSeries | null,
    intensiveCareUnit: AysOperatingNumberTimeSeries | null,
  ): ClinicHandDisinfectionUsage {
    const usage: ClinicHandDisinfectionUsage = {};

    for (const point of normalStations?.points ?? []) {
      const entry = usage[point.year] ?? {};
      entry.millilitersPerPatientDayNormalStation = point.value;
      usage[point.year] = entry;
    }
    for (const point of intensiveCareUnit?.points ?? []) {
      const entry = usage[point.year] ?? {};
      entry.millilitersPerPatientDayIntensiveCareUnit = point.value;
      usage[point.year] = entry;
    }

    return usage;
  }
}
